import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Trash, Info } from "@phosphor-icons/react";
import { Section, cad, errToast } from "@/components/tax/shared";
import { toast } from "sonner";

function ItemRow({ it, rid, locked, onChanged }) {
  const upd = async (body) => {
    try { await api.put(`/tax/returns/${rid}/income-items/${it.id}`, body); onChanged(); } catch (e) { errToast(e); }
  };
  const del = async () => {
    try { await api.delete(`/tax/returns/${rid}/income-items/${it.id}`); onChanged(); } catch (e) { errToast(e); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`tax-income-item-${it.id}`}>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold text-slate-900">{it.label}</p>
        <p className="text-xs text-slate-500">{it.source === "mypayalert" ? `${it.records} record(s) imported from MyPayAlert` : `Added manually · ${it.category}`}</p>
      </div>
      <p className="font-bold text-slate-900 w-28 text-right">{cad(it.amount)}</p>
      {it.source === "mypayalert" ? (
        <select data-testid={`tax-income-coverage-${it.id}`} disabled={locked} value={it.covered_by_slip ? "slip" : "add"}
          onChange={(e) => upd({ covered_by_slip: e.target.value === "slip" })} className="h-9 rounded-md border border-input px-2 text-sm">
          <option value="slip">Already on my T4 / T4A (don't add)</option>
          <option value="add">NOT on any slip — add to my income</option>
        </select>
      ) : (
        !locked && <Button size="sm" variant="ghost" data-testid={`tax-income-delete-${it.id}`} onClick={del}><Trash className="h-4 w-4 text-red-600" /></Button>
      )}
    </div>
  );
}

function AddItem({ rid, onDone }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("other");
  const add = async () => {
    try {
      await api.post(`/tax/returns/${rid}/income-items`, { label, amount: Number(amount), category });
      setLabel(""); setAmount(""); onDone();
    } catch (e) { errToast(e); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-end mt-4">
      <input data-testid="tax-income-new-label" placeholder="Description (e.g. tips, cash job)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 flex-1 min-w-[200px] rounded-md border border-input px-3 text-sm" />
      <input data-testid="tax-income-new-amount" type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 w-32 rounded-md border border-input px-3 text-sm" />
      <select data-testid="tax-income-new-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 rounded-md border border-input px-2 text-sm">
        <option value="employment">Employment income (no slip)</option>
        <option value="other">Other income</option>
      </select>
      <Button size="sm" variant="outline" data-testid="tax-income-add-btn" onClick={add}>Add</Button>
    </div>
  );
}

export function IncomeStep({ ret, locked, refresh }) {
  const [busy, setBusy] = useState(false);
  const items = ret.income_items || [];
  const runImport = async () => {
    setBusy(true);
    try {
      const d = (await api.post(`/tax/returns/${ret.id}/import-income`)).data;
      toast.success(d.imported_sources ? `Imported ${d.imported_sources} income source(s) from MyPayAlert` : `No ${ret.tax_year} MyPayAlert income found`);
      refresh();
    } catch (e) { errToast(e); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="tax-income-step">
      <Section title={`MyPayAlert income for ${ret.tax_year}`} subtitle="We total your logged regular hours, overtime, Paid Duty, court duty and other earnings."
        right={!locked && <Button data-testid="tax-import-income-btn" disabled={busy} onClick={runImport} variant="outline" className="rounded-full">
          <ArrowsClockwise className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {items.some((i) => i.source === "mypayalert") ? "Re-sync" : "Import"}
        </Button>}>
        <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 mb-4" data-testid="tax-duplicate-warning">
          <Info className="h-5 w-5 shrink-0" />
          <p>Income paid through your employer's payroll (including most Paid Duty and court time) is already in T4 Box 14. To avoid double counting, imported items are <strong>not added</strong> unless you mark them as not on any slip. Re-syncing never creates duplicates.</p>
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="tax-income-empty">Nothing imported yet.</p>
          : items.map((it) => <ItemRow key={it.id} it={it} rid={ret.id} locked={locked} onChanged={refresh} />)}
        {!locked && <AddItem rid={ret.id} onDone={refresh} />}
      </Section>
    </div>
  );
}
